import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Settings, Database, Cog } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface SystemMetrics {
  performance_score: number;
  scalability_score: number;
  optimization_score: number;
  recommendations?: string[];
}

interface MetricsMetadata {
  performance_score: number;
  scalability_score: number;
  optimization_score: number;
  recommendations?: string[];
}

export const SystemOptimization = () => {
  const { data: metrics, isLoading } = useQuery({
    queryKey: ['system-optimization'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('analytics_metrics')
        .select('metadata')
        .order('timestamp', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      
      // First cast to unknown, then to MetricsMetadata to avoid type errors
      const metadata = (data?.metadata as unknown) as MetricsMetadata;
      
      return {
        performance_score: metadata?.performance_score || 0,
        scalability_score: metadata?.scalability_score || 0,
        optimization_score: metadata?.optimization_score || 0,
        recommendations: metadata?.recommendations || []
      } as SystemMetrics;
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  if (isLoading) {
    return <Skeleton className="w-full h-48" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          System Optimization Metrics
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Cog className="h-4 w-4" />
              <span>Performance Score</span>
              <span className="ml-auto font-medium">{(metrics.performance_score * 100).toFixed(1)}%</span>
            </div>
            <Progress value={metrics.performance_score * 100} className="h-2" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Database className="h-4 w-4" />
              <span>Scalability Score</span>
              <span className="ml-auto font-medium">{(metrics.scalability_score * 100).toFixed(1)}%</span>
            </div>
            <Progress value={metrics.scalability_score * 100} className="h-2" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Settings className="h-4 w-4" />
              <span>Optimization Score</span>
              <span className="ml-auto font-medium">{(metrics.optimization_score * 100).toFixed(1)}%</span>
            </div>
            <Progress value={metrics.optimization_score * 100} className="h-2" />
          </div>
        </div>

        {metrics.recommendations && metrics.recommendations.length > 0 && (
          <div className="mt-4 text-sm text-muted-foreground">
            <h4 className="font-medium text-foreground">Optimization Recommendations:</h4>
            <ul className="list-disc pl-4 mt-2 space-y-1">
              {metrics.recommendations.map((rec: string, index: number) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};